import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { MapPin, ArrowRight, Clock, Plus, Building2, Pencil, Trash2 } from 'lucide-react';
import { hasPermission } from '../lib/permissions';
import { buildAppDateTimeIso, toAppDateInputValue, toAppTimeInputValue } from '../lib/appDateTime';

const API = process.env.REACT_APP_BACKEND_URL || '';

const SECTORS = [
  'Internação 1',
  'Internação 2',
  'Internação 3',
  'Quarentena',
  'UTI',
  'Recinto Externo',
  'Área de Soltura',
  'Ambulatório'
];

const getSectorOptions = (...selectedValues) => {
  const extraValues = selectedValues
    .filter((value) => value && !SECTORS.includes(value));
  return [...SECTORS, ...extraValues];
};

export default function MovementsTab({ animalId, animal, user, onLocationUpdate }) {
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNewMovementForm, setShowNewMovementForm] = useState(false);
  const [editingMovement, setEditingMovement] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingMovementId, setDeletingMovementId] = useState(null);
  
  // Form state
  const [toSector, setToSector] = useState('');
  const [toBay, setToBay] = useState('');
  const [movedAt, setMovedAt] = useState('');
  const [notes, setNotes] = useState('');
  const [editFromSector, setEditFromSector] = useState('');
  const [editFromBay, setEditFromBay] = useState('');
  const [editToSector, setEditToSector] = useState('');
  const [editToBay, setEditToBay] = useState('');
  const [editMovedAt, setEditMovedAt] = useState('');
  const [editNotes, setEditNotes] = useState('');

  useEffect(() => {
    if (animalId) {
      fetchMovements();
    }
  }, [animalId]);

  const fetchMovements = async () => {
    try {
      const response = await axios.get(`${API}/api/animals/${animalId}/movements`);
      setMovements(response.data);
    } catch (error) {
      console.error('Error fetching movements:', error);
    }
  };

  const toDateTimeLocalValue = (value) => {
    if (!value) return '';
    const dateValue = toAppDateInputValue(value);
    const timeValue = toAppTimeInputValue(value);
    return dateValue && timeValue ? `${dateValue}T${timeValue}` : '';
  };

  const getDefaultMovedAt = () => toDateTimeLocalValue(new Date());

  const buildMovementDateTimeIso = (value) => buildAppDateTimeIso(value.slice(0, 10), value.slice(11, 16));

  const toggleNewMovementForm = () => {
    setShowNewMovementForm((current) => {
      const next = !current;
      if (next && !movedAt) {
        setMovedAt(getDefaultMovedAt());
      }
      if (!next) {
        setToSector('');
        setToBay('');
        setMovedAt('');
        setNotes('');
      }
      return next;
    });
  };

  const openEditMovement = (movement) => {
    setEditingMovement(movement);
    setEditFromSector(movement.from_sector || '');
    setEditFromBay(movement.from_bay || '');
    setEditToSector(movement.to_sector || '');
    setEditToBay(movement.to_bay || '');
    setEditMovedAt(toDateTimeLocalValue(movement.moved_at));
    setEditNotes(movement.notes || '');
  };

  const closeEditMovement = () => {
    setEditingMovement(null);
    setEditFromSector('');
    setEditFromBay('');
    setEditToSector('');
    setEditToBay('');
    setEditMovedAt('');
    setEditNotes('');
  };

  const handleCreateMovement = async (e) => {
    e.preventDefault();
    
    if (!toSector || !toBay || !movedAt) {
      toast.error('Setor, baia de destino e data da movimentação são obrigatórios');
      return;
    }

    // Check if it's the same location
    if (toSector === animal?.allocation_location && toBay === animal?.bay_number) {
      toast.error('O animal já está nesta localização');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/api/movements`, {
        animal_id: animalId,
        to_sector: toSector,
        to_bay: toBay,
        moved_at: buildMovementDateTimeIso(movedAt),
        notes: notes || null
      });
      
      toast.success('Movimentação registrada com sucesso!');
      setShowNewMovementForm(false);
      setToSector('');
      setToBay('');
      setMovedAt('');
      setNotes('');
      fetchMovements();
      
      // Notify parent to refresh animal data
      if (onLocationUpdate) {
        onLocationUpdate();
      }
    } catch (error) {
      console.error('Error creating movement:', error);
      toast.error(error.response?.data?.detail || 'Erro ao registrar movimentação');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveMovementEdit = async () => {
    if (!editingMovement) return;
    if (!editFromSector || !editFromBay || !editToSector || !editToBay || !editMovedAt) {
      toast.error('Preencha origem, destino e data da movimentação');
      return;
    }

    setSavingEdit(true);
    try {
      await axios.put(`${API}/api/movements/${editingMovement.id}`, {
        from_sector: editFromSector,
        from_bay: editFromBay,
        to_sector: editToSector,
        to_bay: editToBay,
        moved_at: buildMovementDateTimeIso(editMovedAt),
        notes: editNotes || null
      });

      toast.success('Histórico de movimentação atualizado');
      closeEditMovement();
      fetchMovements();
      if (onLocationUpdate) {
        onLocationUpdate();
      }
    } catch (error) {
      console.error('Error updating movement:', error);
      toast.error(error.response?.data?.detail || 'Erro ao atualizar movimentação');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteMovement = async (movement) => {
    if (!movement?.id) return;
    if (!window.confirm('Excluir esta movimentação do histórico? A ação ficará registrada na auditoria.')) {
      return;
    }

    setDeletingMovementId(movement.id);
    try {
      await axios.delete(`${API}/api/movements/${movement.id}`);
      toast.success('Histórico de movimentação excluído');
      fetchMovements();
      if (onLocationUpdate) {
        onLocationUpdate();
      }
    } catch (error) {
      console.error('Error deleting movement:', error);
      toast.error(error.response?.data?.detail || 'Erro ao excluir movimentação');
    } finally {
      setDeletingMovementId(null);
    }
  };

  const canCreateMovement = hasPermission(user, 'animals.transfer') || hasPermission(user, 'feeding.record');
  const canEditMovementHistory = user?.role === 'administrador';
  const canDeleteMovementHistory = ['administrador', 'programador'].includes(user?.role);

  return (
    <div className="space-y-6" data-testid="movements-tab">
      {/* Current Location Card */}
      <Card className="border-2 border-emerald-200 bg-emerald-50/50">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-emerald-800">
            <MapPin className="w-5 h-5" />
            Localização Atual
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <Building2 className="w-8 h-8 text-emerald-600" />
                <div>
                  <p className="text-xl font-bold text-emerald-800">
                    {animal?.allocation_location || 'Não definido'}
                  </p>
                  <p className="text-lg text-emerald-600">
                    Baia: <span className="font-semibold">{animal?.bay_number || '-'}</span>
                  </p>
                </div>
              </div>
            </div>
            {canCreateMovement && (
              <Button
                onClick={toggleNewMovementForm}
                className="bg-emerald-600 hover:bg-emerald-700 min-h-[44px]"
                data-testid="new-movement-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                {showNewMovementForm ? 'Cancelar' : 'Nova Movimentação'}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* New Movement Form */}
      {showNewMovementForm && (
        <Card className="border-emerald-200" data-testid="new-movement-form">
          <CardHeader>
            <CardTitle className="text-emerald-800">Registrar Movimentação</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreateMovement} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* From (current location - readonly) */}
                <div className="p-4 bg-gray-100 rounded-lg">
                  <Label className="text-gray-600">De (Atual)</Label>
                  <p className="font-semibold text-lg mt-1">
                    {animal?.allocation_location} - {animal?.bay_number}
                  </p>
                </div>

                {/* To (destination) */}
                <div className="p-4 bg-emerald-50 rounded-lg border-2 border-emerald-200">
                  <Label className="text-emerald-700 font-semibold">Para (Destino) *</Label>
                  <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <Select value={toSector} onValueChange={setToSector}>
                      <SelectTrigger data-testid="to-sector-select">
                        <SelectValue placeholder="Setor" />
                      </SelectTrigger>
                      <SelectContent>
                        {SECTORS.map(sector => (
                          <SelectItem key={sector} value={sector}>{sector}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      value={toBay}
                      onChange={(e) => setToBay(e.target.value)}
                      placeholder="Baia/Número"
                      data-testid="to-bay-input"
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label>Data/Hora da movimentação *</Label>
                <Input
                  type="datetime-local"
                  value={movedAt}
                  onChange={(e) => setMovedAt(e.target.value)}
                  data-testid="movement-date-input"
                />
              </div>

              <div>
                <Label>Observações</Label>
                <Input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Motivo da movimentação (opcional)"
                  data-testid="movement-notes-input"
                />
              </div>

              <div className="flex flex-col sm:flex-row justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={toggleNewMovementForm}
                  className="min-h-[44px]"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-emerald-600 hover:bg-emerald-700 min-h-[44px]"
                  data-testid="submit-movement-btn"
                >
                  {loading ? 'Salvando...' : 'Confirmar Movimentação'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Movement History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-emerald-800">
            <Clock className="w-5 h-5" />
            Histórico de Movimentações
          </CardTitle>
        </CardHeader>
        <CardContent>
          {movements.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <MapPin className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p>Nenhuma movimentação registrada</p>
              <p className="text-sm">O animal permanece na localização inicial desde a admissão</p>
            </div>
          ) : (
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-emerald-200 hidden sm:block" />
              
              <div className="space-y-4">
                {movements.map((movement, index) => (
                  <div 
                    key={movement.id} 
                    className="relative flex items-start gap-4"
                    data-testid={`movement-item-${index}`}
                  >
                    {/* Timeline dot */}
                    <div className="hidden sm:flex items-center justify-center w-8 h-8 rounded-full bg-emerald-100 border-2 border-emerald-500 z-10 flex-shrink-0">
                      <MapPin className="w-4 h-4 text-emerald-600" />
                    </div>
                    
                    {/* Movement card */}
                    <Card className="flex-1 border-l-4 border-l-emerald-500">
                      <CardContent className="pt-4">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                          {/* From -> To */}
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="outline" className="text-gray-600">
                              {movement.from_sector} - {movement.from_bay}
                            </Badge>
                            <ArrowRight className="w-4 h-4 text-emerald-600" />
                            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">
                              {movement.to_sector} - {movement.to_bay}
                            </Badge>
                          </div>
                          {(canEditMovementHistory || canDeleteMovementHistory) && (
                            <div className="flex flex-wrap gap-2">
                              {canEditMovementHistory && (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openEditMovement(movement)}
                                  data-testid={`edit-movement-${movement.id}`}
                                >
                                  <Pencil className="w-4 h-4 mr-1" />
                                  Editar
                                </Button>
                              )}
                              {canDeleteMovementHistory && (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700"
                                  onClick={() => handleDeleteMovement(movement)}
                                  disabled={deletingMovementId === movement.id}
                                  data-testid={`delete-movement-${movement.id}`}
                                >
                                  <Trash2 className="w-4 h-4 mr-1" />
                                  {deletingMovementId === movement.id ? 'Excluindo...' : 'Excluir'}
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                        
                        <div className="mt-2 flex flex-col sm:flex-row sm:items-center gap-2 text-sm text-gray-600">
                          <span>
                            {new Date(movement.moved_at).toLocaleDateString('pt-BR', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                          {movement.notes && (
                            <>
                              <span className="hidden sm:inline">•</span>
                              <span className="italic">{movement.notes}</span>
                            </>
                          )}
                        </div>
                        {movement.updated_at && (
                          <p className="mt-2 text-xs text-slate-500">
                            Editado por {movement.updated_by_name || movement.updated_by || 'N/A'} em {new Date(movement.updated_at).toLocaleString('pt-BR')}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={Boolean(editingMovement)} onOpenChange={(open) => !open && closeEditMovement()}>
        <DialogContent className="w-[calc(100vw-1.5rem)] max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-emerald-800">Editar histórico de movimentação</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                <Label>De (Origem) *</Label>
                <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <Select value={editFromSector} onValueChange={setEditFromSector}>
                    <SelectTrigger data-testid="edit-from-sector-select">
                      <SelectValue placeholder="Setor" />
                    </SelectTrigger>
                    <SelectContent>
                      {getSectorOptions(editFromSector).map(sector => (
                        <SelectItem key={sector} value={sector}>{sector}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    value={editFromBay}
                    onChange={(e) => setEditFromBay(e.target.value)}
                    placeholder="Baia/Número"
                    data-testid="edit-from-bay-input"
                  />
                </div>
              </div>

              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
                <Label>Para (Destino) *</Label>
                <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <Select value={editToSector} onValueChange={setEditToSector}>
                    <SelectTrigger data-testid="edit-to-sector-select">
                      <SelectValue placeholder="Setor" />
                    </SelectTrigger>
                    <SelectContent>
                      {getSectorOptions(editToSector).map(sector => (
                        <SelectItem key={sector} value={sector}>{sector}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    value={editToBay}
                    onChange={(e) => setEditToBay(e.target.value)}
                    placeholder="Baia/Número"
                    data-testid="edit-to-bay-input"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <Label>Data/Hora *</Label>
                <Input
                  type="datetime-local"
                  value={editMovedAt}
                  onChange={(e) => setEditMovedAt(e.target.value)}
                  data-testid="edit-movement-date-input"
                />
              </div>
              <div>
                <Label>Observações</Label>
                <Input
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  placeholder="Motivo/observação"
                  data-testid="edit-movement-notes-input"
                />
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={closeEditMovement} disabled={savingEdit}>
              Cancelar
            </Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleSaveMovementEdit} disabled={savingEdit}>
              {savingEdit ? 'Salvando...' : 'Salvar alteração'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
